import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboardIcon,
  CalendarIcon,
  CarIcon,
  TruckIcon,
  UsersIcon,
  DollarSignIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const navItems: NavItem[] = [
  {
    label: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboardIcon,
  },
  {
    label: "Booking Management",
    href: "/bookings",
    icon: CalendarIcon,
  },
  {
    label: "Driver Management",
    href: "/drivers",
    icon: CarIcon,
  },
  {
    label: "Vehicle Management",
    href: "/vehicles",
    icon: TruckIcon,
  },
  {
    label: "Customer Management",
    href: "/customers",
    icon: UsersIcon,
  },
  {
    label: "Pricing Setup",
    href: "/pricing",
    icon: DollarSignIcon,
  },
];

interface SidebarNavProps {
  isCollapsed?: boolean;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function SidebarNav({
  isCollapsed = false,
  isMobileOpen = false,
  onMobileClose,
}: SidebarNavProps) {
  const location = useLocation();

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-50 flex h-screen flex-col bg-gradient-to-b from-[#001D12] via-[#002818] to-[#001D12] text-white shadow-2xl",
          "w-64 transition-[width,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] lg:w-[var(--admin-sidebar-width)]",
          // Mobile: slide in from left
          "transform will-change-transform lg:transform-none",
          isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Subtle Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 2px 2px, rgba(197, 154, 74, 0.4) 1px, transparent 0)",
              backgroundSize: "32px 32px",
            }}
          />
        </div>

        {/* Logo */}
        <div className="relative h-20 lg:h-26 pt-4 flex items-center justify-center px-6 ">
          <Link
            to="/dashboard"
            className="flex items-center justify-center w-full"
          >
            <img
              src="https://assets.polymet.ai/educational-beige-754806"
              alt="ARK Arrival"
              className={cn(
                "transition-[width,opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
                isCollapsed ? "lg:w-12" : "w-[120px]"
              )}
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="relative flex-1 px-4 py-8">
          <ul className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname.startsWith(item.href);

              return (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    onClick={onMobileClose}
                    className={cn(
                      "group relative flex items-center gap-3 rounded-xl px-4 py-3.5 text-sm font-semibold transition-colors duration-150",
                      // Collapsed: drop the gap too, or it still offsets the icon from the hidden label.
                      isCollapsed && "lg:justify-center lg:gap-0 lg:px-3",
                      isActive
                        ? "bg-gradient-to-r from-[#C59A4A] to-[#B8904A] text-white shadow-lg shadow-[#C59A4A]/30"
                        : "text-white/60 hover:text-white hover:bg-white/5"
                    )}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <Icon
                      className={cn(
                        "w-5 h-5 shrink-0 transition-transform duration-200",
                        isActive ? "" : "group-hover:scale-110"
                      )}
                    />
                    <span
                      className={cn(
                        "overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200",
                        isCollapsed ? "lg:max-w-0 lg:opacity-0" : "max-w-[12rem] opacity-100"
                      )}
                    >
                      {item.label}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="relative p-6 border-t border-white/10">
          <div className="flex items-center gap-2 justify-center mb-2">
            <div className="w-8 h-[1px] bg-gradient-to-r from-transparent to-white/20" />
            <div className="w-1 h-1 rounded-full bg-[#C59A4A]/50" />
            <div className="w-8 h-[1px] bg-gradient-to-l from-transparent to-white/20" />
          </div>
          <div className="text-xs text-white/40 text-center font-medium">
            © 2026 ARK Arrival
          </div>
        </div>
      </aside>
    </>
  );
}
